import {
  BaseQueryApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import * as Sentry from '@sentry/react';
import Cookies from 'js-cookie';

import { userLoggedOut } from '../../state/reducers/root';
import { getFrontAuthSecret } from '../../utils/frontAuthSecret';
import { captureError } from '../monitoring/sentry';

const baseQuery = fetchBaseQuery({
  baseUrl: `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}`,
  credentials: 'include',
  prepareHeaders: (headers) => {
    const csrftoken = Cookies.get('csrftoken');
    if (csrftoken) {
      headers.set('X-CSRFToken', csrftoken);
    }
    const frontSecret = getFrontAuthSecret();
    if (frontSecret) {
      headers.set('X-Front-Auth-Secret', frontSecret);
    }
    return headers;
  },
});

/**
 * Check if a 403 error is a CSRF token issue.
 * CSRF errors occur when the token is missing, invalid, or expired.
 */
const isCsrfError = (errorData: unknown): boolean => {
  if (!errorData || typeof errorData !== 'object') return false;
  const data = errorData as { detail?: string };
  return Boolean(data.detail?.toUpperCase().includes('CSRF'));
};

const captureSentryException = (
  baseQueryResult: { error?: FetchBaseQueryError },
  args: string | FetchArgs,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extraOptions?: any,
) => {
  if (baseQueryResult.error) {
    const { error } = baseQueryResult as { error: FetchBaseQueryError };
    const url = typeof args === 'string' ? args : args.url;
    const method = typeof args === 'string' ? 'GET' : (args.method ?? 'GET');

    Sentry.addBreadcrumb({
      category: 'api',
      message: `${method} ${url} failed with ${error.status}`,
      level: 'error',
      data: { status: error.status },
    });

    captureError(new Error(`API Error: ${error.status} ${method} ${url}`), {
      tags: {
        http_status: String(error.status),
        http_method: method,
      },
      extra: {
        endpoint: url,
        response_data: error.data,
        extra_options: extraOptions,
      },
    });
  }
};

/**
 * Clear auth cookies and dispatch logout action.
 * Cookies are cleared here (not in the reducer) to keep reducers pure.
 */
const forceLogout = (api: BaseQueryApi) => {
  Cookies.remove('sessionid');
  Cookies.remove('csrftoken');
  api.dispatch(userLoggedOut());
};

const baseQueryWithErrorHandling = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extraOptions?: any,
) => {
  let result = await baseQuery(args, api, extraOptions);

  // Handle 401 Unauthorized - retry once, then force logout
  if (result.error?.status === 401) {
    result = await baseQuery(args, api, extraOptions);
    if (result.error?.status === 401) {
      Sentry.addBreadcrumb({
        category: 'auth',
        message: '401 Unauthorized - forcing logout after retry',
        level: 'warning',
      });
      forceLogout(api);
    }
  } else if (result.error?.status === 403 && isCsrfError(result.error.data)) {
    // Handle 403 CSRF errors - clear stale token, retry once, then force logout
    Cookies.remove('csrftoken');

    // Retry - Django's error response may have set a fresh CSRF cookie
    result = await baseQuery(args, api, extraOptions);
    if (result.error?.status === 403 && isCsrfError(result.error.data)) {
      // Persistent CSRF error after token refresh - force logout
      Sentry.addBreadcrumb({
        category: 'auth',
        message: '403 CSRF error - forcing logout after retry',
        level: 'warning',
        data: { detail: (result.error.data as { detail?: string })?.detail },
      });
      forceLogout(api);
    } else if (result.error?.status === 401) {
      // Session expired during CSRF retry - force logout
      Sentry.addBreadcrumb({
        category: 'auth',
        message: '401 after CSRF retry - forcing logout',
        level: 'warning',
      });
      forceLogout(api);
    }
    // Note: 403 non-CSRF (permissions) or other errors (404/500) after retry
    // are not auth issues - don't logout, just let them be logged to Sentry
  }

  if (result.error) {
    const skipStatuses: number[] = extraOptions?.skipSentryOnStatus ?? [];
    if (!skipStatuses.includes(result.error.status as number)) {
      captureSentryException(result, args, extraOptions);
    }
  }

  return result;
};

export { baseQueryWithErrorHandling };
